// /reports/payables-ageing — Payables Ageing (QA PU4-010, docs/purchase-gaps-plan.md §5.8)
//
// What is owed to each supplier today, in the tenant currency, by days past the
// bill's due date on its store's clock. Due today counts as not yet due (PU3-003).

import { useState } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  KpiIcons,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { PayablesAgeingAmounts, PayablesAgeingRow, ReportKpiTile } from '@/features/reports';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

type Row = PayablesAgeingRow & { id: string };

const BUCKETS: { key: keyof PayablesAgeingAmounts; label: string; tone: string }[] = [
  { key: 'not_due', label: 'Not yet due', tone: 'text-success-600 dark:text-success-400' },
  { key: 'd0_30', label: '1-30 days', tone: 'text-warning-600 dark:text-warning-400' },
  { key: 'd31_60', label: '31-60 days', tone: 'text-orange-600 dark:text-orange-400' },
  { key: 'd61_90', label: '61-90 days', tone: 'text-danger-500 dark:text-danger-400' },
  { key: 'd90_plus', label: '90+ days', tone: 'text-danger-700 dark:text-danger-300' },
];

export function PayablesAgeingReportPage() {
  // The bar needs a range value; ageing is "as of today" and hides it.
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const currency = useTenantCurrency();
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'payables-ageing', tenantId, locationId ?? 'all'],
    queryFn: () => reportsApi.getPayablesAgeing(locationId),
    staleTime: 30_000,
  });

  const rows: Row[] = (data?.rows ?? []).map((r) => ({ ...r, id: r.supplier_id ?? r.supplier_name }));

  const columns: ColumnDef<Row>[] = [
    {
      key: 'supplier_name',
      header: 'Supplier',
      render: (_v, r) => (
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{r.supplier_name}</span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {r.bill_count} bill(s){r.oldest_due_date ? ` · oldest due ${r.oldest_due_date}` : ''}
          </span>
        </div>
      ),
    },
    ...BUCKETS.map((b): ColumnDef<Row> => ({
      key: b.key,
      header: b.label,
      numeric: true,
      render: (_v, r) => <span className={`tabular-nums ${r[b.key] ? b.tone : 'text-neutral-400'}`}>{formatPaise(r[b.key], currency)}</span>,
    })),
    { key: 'total', header: 'Total owed', numeric: true, render: (_v, r) => <span className="text-sm font-bold tabular-nums">{formatPaise(r.total, currency)}</span> },
  ];

  const overdue = data ? data.totals.total - data.totals.not_due : 0;
  const kpis: ReportKpiTile[] = data
    ? [
        { id: 'total', title: 'Total owed', value: formatPaise(data.totals.total, currency), subLabel: `${data.totals.bill_count} open bill(s)`, icon: KpiIcons.wallet, tone: 'brand' },
        { id: 'not-due', title: 'Not yet due', value: formatPaise(data.totals.not_due, currency), icon: KpiIcons.clock, tone: 'success' },
        { id: 'overdue', title: 'Overdue', value: formatPaise(overdue, currency), icon: KpiIcons.trendDown, tone: 'warning' },
        { id: 'late90', title: '90+ days late', value: formatPaise(data.totals.d90_plus, currency), icon: KpiIcons.fire, tone: 'danger' },
      ]
    : [];

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      window.print();
      return;
    }
    if (fmt === 'csv') {
      const subunit = getSubunitMultiplier(currency);
      const decimals = Math.round(Math.log10(subunit));
      const amount = (v: number) => (v / subunit).toFixed(decimals);
      downloadCsv(
        `payables-ageing-${new Date().toISOString().slice(0, 10)}.csv`,
        [
          { header: 'Supplier', get: (r: Row) => r.supplier_name },
          { header: 'Tax ID', get: (r: Row) => r.supplier_gstin ?? '' },
          { header: 'Open bills', get: (r: Row) => r.bill_count },
          { header: 'Oldest due date', get: (r: Row) => r.oldest_due_date ?? '' },
          ...BUCKETS.map((b) => ({ header: `${b.label} (${currency})`, get: (r: Row) => amount(r[b.key]) })),
          { header: `Total (${currency})`, get: (r: Row) => amount(r.total) },
        ],
        rows,
      );
      return;
    }
    shareReportLink(fmt, 'Orio360 Payables Ageing', `Owed ${formatPaise(data?.totals.total ?? 0, currency)} • overdue ${formatPaise(overdue, currency)}`);
  };

  return (
    <ReportPageShell
      title="Payables Ageing"
      subtitle="What you owe each supplier, by how late the bill is."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Payables Ageing' }]}
      accent="warning-danger"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} showTimeRange={false} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow tiles={kpis} />
          <ReportTable title="Open balance by supplier" columns={columns} data={rows} emptyTitle="Nothing owed to suppliers" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
